import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Download, Upload, Mail, TestTube, Sparkles, Wand2, Building2, Home, ShieldCheck, Database, SlidersHorizontal, UserRound, ShieldBan, Search, ChevronRight } from "lucide-react";
import { PinSettingsSection } from "@/components/PinGuard";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";

function StartupPageSection() {
  const qc = useQueryClient();
  const { data: cfg } = useQuery({
    queryKey: ["business-config"],
    queryFn: async () => (await api.get("/business/config")).data,
  });
  const current = cfg?.startup_page || "calendar";
  const saveMut = useMutation({
    mutationFn: async (val) => (await api.post("/business/config", { ...cfg, startup_page: val })).data,
    onSuccess: (_d, val) => {
      try { localStorage.setItem("stellar_startup_page", val); } catch (_) { /* ignore */ }
      toast.success("Startseite gespeichert");
      qc.invalidateQueries({ queryKey: ["business-config"] });
    },
    onError: (e) => toast.error(apiError(e)),
  });

  const OPTIONS = [
    { id: "calendar",  label: "Kalender",         hint: "Öffnet direkt die Planung der nächsten vier Wochen. Standard und empfohlen." },
    { id: "dashboard", label: "Übersicht",        hint: "Enthält Umsatz- und Rechnungsstatistiken — nicht empfohlen, wenn Kunden das Bildschirm sehen." },
    { id: "jobs",      label: "Laufende Aufträge", hint: "Direkt zu den aktiven Werkstattaufträgen." },
  ];

  return (
    <div className="premium-card stellar-settings-card p-5" data-testid="startup-page-section">
      <h2 className="font-display font-semibold text-lg mb-1 flex items-center gap-2">
        <Home className="h-4 w-4" /> Startseite
      </h2>
      <p className="text-sm text-muted-foreground mb-4">
        Welche Seite soll nach dem Anmelden zuerst geöffnet werden?
      </p>
      <div className="space-y-2">
        {OPTIONS.map((o) => (
          <label
            key={o.id}
            className={`flex items-start gap-3 rounded-md border p-3 cursor-pointer transition-colors ${
              current === o.id ? "border-primary bg-primary/5" : "border-border/60 hover:bg-muted/40"
            }`}
            data-testid={`startup-option-${o.id}`}
          >
            <input
              type="radio"
              name="startup-page"
              className="mt-1 accent-primary"
              checked={current === o.id}
              onChange={() => saveMut.mutate(o.id)}
              disabled={saveMut.isPending}
            />
            <div>
              <div className="text-sm font-medium">{o.label}{o.id === "calendar" && <span className="ml-2 text-[10px] uppercase tracking-widest text-primary">Standard</span>}</div>
              <div className="text-xs text-muted-foreground mt-0.5">{o.hint}</div>
            </div>
          </label>
        ))}
      </div>
    </div>
  );
}

function BusinessConfigSection() {
  const qc = useQueryClient();
  const { data: cfg } = useQuery({
    queryKey: ["business-config"],
    queryFn: async () => (await api.get("/business/config")).data,
  });
  const [form, setForm] = useState(null);
  useEffect(() => { if (cfg) setForm(cfg); }, [cfg]);
  const saveMut = useMutation({
    mutationFn: async () => (await api.post("/business/config", form)).data,
    onSuccess: () => { toast.success("Firmendaten gespeichert"); qc.invalidateQueries({ queryKey: ["business-config"] }); },
    onError: (e) => toast.error(apiError(e)),
  });
  if (!form) return null;
  const F = (k, v) => setForm({ ...form, [k]: v });
  return (
    <div className="premium-card stellar-settings-card p-5">
      <h2 className="font-display font-semibold text-lg mb-1 flex items-center gap-2">
        <Building2 className="h-4 w-4" /> Firmendaten (für Rechnungen)
      </h2>
      <p className="text-sm text-muted-foreground mb-4">Diese Angaben erscheinen im Kopf jeder Rechnung.</p>
      <div className="grid sm:grid-cols-2 gap-3">
        <div className="sm:col-span-2"><Label>Firmenname</Label><Input value={form.name} onChange={(e) => F("name", e.target.value)} data-testid="bc-name" /></div>
        <div className="sm:col-span-2"><Label>Straße & Hausnummer</Label><Input value={form.street} onChange={(e) => F("street", e.target.value)} /></div>
        <div><Label>PLZ</Label><Input value={form.zip} onChange={(e) => F("zip", e.target.value)} /></div>
        <div><Label>Ort</Label><Input value={form.city} onChange={(e) => F("city", e.target.value)} /></div>
        <div><Label>Telefon</Label><Input value={form.phone} onChange={(e) => F("phone", e.target.value)} /></div>
        <div><Label>E-Mail</Label><Input value={form.email} onChange={(e) => F("email", e.target.value)} /></div>
        <div><Label>Steuer-Nr.</Label><Input value={form.tax_id} onChange={(e) => F("tax_id", e.target.value)} /></div>
        <div><Label>USt-IdNr.</Label><Input value={form.vat_id} onChange={(e) => F("vat_id", e.target.value)} /></div>
        <div className="sm:col-span-2"><Label>Kontoinhaber</Label><Input value={form.account_holder || ""} onChange={(e) => F("account_holder", e.target.value)} data-testid="bc-account-holder" /></div>
        <div><Label>Bank</Label><Input value={form.bank_name} onChange={(e) => F("bank_name", e.target.value)} /></div>
        <div><Label>IBAN</Label><Input value={form.iban} onChange={(e) => F("iban", e.target.value)} data-testid="bc-iban" /></div>
        <div><Label>BIC</Label><Input value={form.bic} onChange={(e) => F("bic", e.target.value)} /></div>
        <div><Label>Zahlungsziel (Tage)</Label><Input type="number" value={form.default_payment_terms_days} onChange={(e) => F("default_payment_terms_days", Number(e.target.value))} /></div>
      </div>
      <div className="mt-3">
        <label className="inline-flex items-center gap-2 text-sm">
          <input type="checkbox" checked={form.kleinunternehmer} onChange={(e) => F("kleinunternehmer", e.target.checked)} data-testid="bc-kleinunternehmer" />
          Kleinunternehmer gemäß §19 UStG (0% USt.)
        </label>
      </div>
      <div className="mt-3">
        <Label>Fußzeile (Rechnungen)</Label>
        <Textarea rows={2} value={form.invoice_footer} onChange={(e) => F("invoice_footer", e.target.value)} placeholder="z.B. Vielen Dank für Ihr Vertrauen!" />
      </div>
      <div className="mt-4">
        <Button onClick={() => saveMut.mutate()} data-testid="save-business-config">Speichern</Button>
      </div>
    </div>
  );
}

function BackfillSection() {
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const run = async (dry) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/vehicles/backfill-appointments?dry_run=${dry}`);
      setResult(data);
      if (!dry) toast.success(`${data.applied} Termine mit Fahrzeugen verknüpft`);
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };
  return (
    <div className="premium-card stellar-settings-card p-5">
      <h2 className="font-display font-semibold text-lg mb-1 flex items-center gap-2">
        <Wand2 className="h-4 w-4" /> Alte Termine Fahrzeugen zuordnen
      </h2>
      <p className="text-sm text-muted-foreground mb-4">
        Bestehende Termine (vor Fahrzeug-Modul) werden über das Kennzeichen automatisch mit angelegten Fahrzeugen verknüpft.
      </p>
      <div className="flex flex-wrap gap-2">
        <Button variant="outline" onClick={() => run(true)} disabled={busy} data-testid="backfill-dry">Vorschau</Button>
        <Button onClick={() => run(false)} disabled={busy || !result} data-testid="backfill-apply">Anwenden</Button>
      </div>
      {result && (
        <div className="mt-3 text-xs text-muted-foreground space-y-1">
          <div>Termine ohne Fahrzeug: <span className="font-medium text-foreground">{result.total_missing}</span></div>
          <div>Übereinstimmungen gefunden: <span className="font-medium text-foreground">{result.matched}</span></div>
          <div>Mehrdeutige (übersprungen): <span className="font-medium text-foreground">{result.ambiguous_skipped}</span></div>
          {!result.dry_run && <div>Angewendet: <span className="font-medium text-emerald-600 dark:text-emerald-400">{result.applied}</span></div>}
        </div>
      )}
    </div>
  );
}

function EmailSection() {
  const qc = useQueryClient();
  const [blockedOpen, setBlockedOpen] = useState(false);
  const [blockedSearch, setBlockedSearch] = useState("");
  const { data: cfg } = useQuery({
    queryKey: ["email-config"],
    queryFn: async () => (await api.get("/email/config")).data,
  });
  const [form, setForm] = useState({ host: "", port: 993, user: "", password: "", use_ssl: true, folder: "INBOX" });
  useEffect(() => {
    if (cfg) setForm((f) => ({ ...f, ...cfg, password: "" }));
  }, [cfg]);

  const saveMut = useMutation({
    mutationFn: async () => (await api.post("/email/config", form)).data,
    onSuccess: () => { toast.success("Gespeichert"); qc.invalidateQueries({ queryKey: ["email-config"] }); },
    onError: (e) => toast.error(apiError(e)),
  });
  const testMut = useMutation({
    mutationFn: async () => (await api.post("/email/test")).data,
    onSuccess: (d) => d.ok ? toast.success(`Verbindung erfolgreich (${d.folder})`) : toast.error(`Fehler: ${d.error}`),
    onError: (e) => toast.error(apiError(e)),
  });
  const removeMut = useMutation({
    mutationFn: async () => (await api.delete("/email/config")).data,
    onSuccess: () => { toast.success("Entfernt"); qc.invalidateQueries({ queryKey: ["email-config"] }); setForm({ host: "", port: 993, user: "", password: "", use_ssl: true, folder: "INBOX" }); },
  });
  const { data: blockedSenders = [] } = useQuery({
    queryKey: ["email-blocked-senders"],
    queryFn: async () => (await api.get("/email/blocked-senders")).data,
  });
  const unblockMut = useMutation({
    mutationFn: async (id) => (await api.delete(`/email/blocked-senders/${id}`)).data,
    onSuccess: () => {
      toast.success("Absender freigegeben");
      qc.invalidateQueries({ queryKey: ["email-blocked-senders"] });
    },
    onError: (e) => toast.error(apiError(e)),
  });
  const unblockAllMut = useMutation({
    mutationFn: async () => (await api.delete("/email/blocked-senders")).data,
    onSuccess: (data) => {
      toast.success(`${data.deleted || 0} Absender freigegeben`);
      qc.invalidateQueries({ queryKey: ["email-blocked-senders"] });
      setBlockedOpen(false);
    },
    onError: (e) => toast.error(apiError(e)),
  });
  const visibleBlockedSenders = blockedSenders.filter((sender) =>
    (sender.email || "").toLowerCase().includes(blockedSearch.trim().toLowerCase())
  );

  return (
    <div className="premium-card stellar-settings-card p-5">
      <h2 className="font-display font-semibold text-lg mb-1 flex items-center gap-2"><Mail className="h-4 w-4" /> E-Mail-Konto (IMAP)</h2>
      <p className="text-sm text-muted-foreground mb-4">
        Verbinden Sie ein E-Mail-Postfach, um eingehende Nachrichten in der App zu lesen. Bei Gmail bitte ein <a className="underline" href="https://support.google.com/accounts/answer/185833" target="_blank" rel="noreferrer">App-Passwort</a> verwenden.
      </p>
      <div className="grid sm:grid-cols-2 gap-3">
        <div><Label>IMAP-Server</Label><Input value={form.host} onChange={(e) => setForm({...form, host: e.target.value})} placeholder="imap.ionos.de" data-testid="imap-host" /></div>
        <div><Label>Port</Label><Input type="number" value={form.port} onChange={(e) => setForm({...form, port: Number(e.target.value)})} data-testid="imap-port" /></div>
        <div><Label>Benutzer (E-Mail)</Label><Input value={form.user} onChange={(e) => setForm({...form, user: e.target.value})} placeholder="kontakt@auto-stellar.de" data-testid="imap-user" /></div>
        <div>
          <Label>Passwort {cfg?.password_set && <span className="text-xs text-muted-foreground">(gespeichert)</span>}</Label>
          <Input type="password" value={form.password} onChange={(e) => setForm({...form, password: e.target.value})} data-testid="imap-password" />
        </div>
        <div><Label>Ordner</Label><Input value={form.folder} onChange={(e) => setForm({...form, folder: e.target.value})} data-testid="imap-folder" /></div>
        <div className="flex items-end gap-2">
          <label className="inline-flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.use_ssl} onChange={(e) => setForm({...form, use_ssl: e.target.checked})} data-testid="imap-ssl" /> SSL/TLS
          </label>
        </div>
      </div>

      <div className="mt-6 pt-4 border-t border-border/60">
        <h3 className="text-sm font-semibold mb-3">SMTP (für Versand)</h3>
        <div className="grid sm:grid-cols-2 gap-3">
          <div><Label>SMTP-Server</Label><Input value={form.smtp_host} onChange={(e) => setForm({...form, smtp_host: e.target.value})} placeholder="smtp.ionos.de" data-testid="smtp-host" /></div>
          <div><Label>SMTP-Port</Label><Input type="number" value={form.smtp_port} onChange={(e) => setForm({...form, smtp_port: Number(e.target.value)})} data-testid="smtp-port" /></div>
          <div><Label>SMTP-Benutzer</Label><Input value={form.smtp_user} onChange={(e) => setForm({...form, smtp_user: e.target.value})} placeholder="(leer = IMAP-Benutzer)" data-testid="smtp-user" /></div>
          <div>
            <Label>SMTP-Passwort {cfg?.smtp_password_set && <span className="text-xs text-muted-foreground">(gespeichert)</span>}</Label>
            <Input type="password" value={form.smtp_password} onChange={(e) => setForm({...form, smtp_password: e.target.value})} data-testid="smtp-password" />
          </div>
          <div><Label>Absender-Name</Label><Input value={form.from_name} onChange={(e) => setForm({...form, from_name: e.target.value})} data-testid="smtp-from-name" /></div>
          <div className="flex items-end">
            <label className="inline-flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.smtp_use_tls} onChange={(e) => setForm({...form, smtp_use_tls: e.target.checked})} data-testid="smtp-tls" /> STARTTLS (Port 587) / SSL (Port 465)
            </label>
          </div>
        </div>
      </div>
      <div className="flex flex-wrap gap-2 mt-4">
        <Button onClick={() => saveMut.mutate()} disabled={!form.host || !form.user || (!form.password && !cfg?.password_set)} data-testid="imap-save">Speichern</Button>
        <Button variant="outline" onClick={() => testMut.mutate()} disabled={!cfg} data-testid="imap-test"><TestTube className="h-4 w-4 mr-1" /> Verbindung testen</Button>
        {cfg && <Button variant="ghost" className="text-destructive" onClick={() => { if (confirm("Konfiguration entfernen?")) removeMut.mutate(); }} data-testid="imap-delete">Entfernen</Button>}
      </div>
      <div className="mt-6 border-t border-border/60 pt-4" data-testid="blocked-senders-section">
        <button
          type="button"
          onClick={() => setBlockedOpen(true)}
          className="flex w-full items-center gap-3 rounded-xl border border-border/60 bg-muted/20 p-3 text-left transition hover:bg-muted/40"
          data-testid="open-blocked-senders"
        >
          <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-amber-50 text-amber-700"><ShieldBan className="h-5 w-5" /></span>
          <span className="min-w-0 flex-1">
            <span className="block text-sm font-semibold">Spam & blockierte Absender</span>
            <span className="block text-xs text-muted-foreground">{blockedSenders.length === 0 ? "Keine blockierten Adressen" : `${blockedSenders.length} blockierte ${blockedSenders.length === 1 ? "Adresse" : "Adressen"}`}</span>
          </span>
          <span className="inline-flex min-w-7 items-center justify-center rounded-lg bg-amber-100 px-2 py-1 text-xs font-bold text-amber-800">{blockedSenders.length}</span>
          <ChevronRight className="h-4 w-4 text-muted-foreground" />
        </button>
      </div>

      <Dialog open={blockedOpen} onOpenChange={setBlockedOpen}>
        <DialogContent className="w-[calc(100vw-1rem)] sm:max-w-xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><ShieldBan className="h-5 w-5 text-amber-700" /> Spam & blockierte Absender</DialogTitle>
          </DialogHeader>
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input value={blockedSearch} onChange={(event) => setBlockedSearch(event.target.value)} placeholder="Adresse suchen…" className="pl-9" data-testid="blocked-senders-search" />
          </div>
          <div className="max-h-[55vh] space-y-2 overflow-y-auto pr-1">
            {visibleBlockedSenders.length === 0 ? (
              <div className="rounded-xl border border-dashed p-6 text-center text-sm text-muted-foreground">
                {blockedSenders.length === 0 ? "Keine blockierten Absender." : "Keine passende Adresse gefunden."}
              </div>
            ) : visibleBlockedSenders.map((sender) => (
              <div key={sender.id} className="flex min-w-0 items-center gap-2 rounded-xl border border-border/60 bg-muted/20 px-3 py-2.5">
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-medium">{sender.email}</div>
                  {sender.blocked_at && <div className="text-xs text-muted-foreground">Blockiert am {new Date(sender.blocked_at).toLocaleDateString("de-DE")}</div>}
                </div>
                <Button variant="outline" size="sm" onClick={() => unblockMut.mutate(sender.id)} disabled={unblockMut.isPending} data-testid={`unblock-sender-${sender.id}`}>Freigeben</Button>
              </div>
            ))}
          </div>
          <DialogFooter className="gap-2 sm:justify-between">
            {blockedSenders.length > 0 && (
              <Button type="button" variant="ghost" className="text-destructive" onClick={() => { if (confirm("Alle blockierten Absender freigeben?")) unblockAllMut.mutate(); }} disabled={unblockAllMut.isPending} data-testid="unblock-all-senders">Alle freigeben</Button>
            )}
            <Button type="button" variant="outline" onClick={() => setBlockedOpen(false)}>Schließen</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function Settings() {
  const { user } = useAuth();
  const fileRef = useRef(null);
  const [replace, setReplace] = useState(false);
  const [busy, setBusy] = useState(false);

  const exportJson = async () => {
    try {
      const { data } = await api.get("/backup/export");
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const dt = new Date().toISOString().slice(0, 10);
      a.href = url; a.download = `stellar-backup-${dt}.json`; a.click();
      URL.revokeObjectURL(url);
      toast.success("Backup heruntergeladen");
    } catch (e) { toast.error(apiError(e)); }
  };

  const importJson = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setBusy(true);
    try {
      const text = await file.text();
      const json = JSON.parse(text);
      const res = await api.post("/backup/import", { ...json, replace });
      toast.success(`Importiert: ${JSON.stringify(res.data.counts)}`);
    } catch (err) { toast.error(apiError(err)); }
    finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  return (
    <div className="stellar-page max-w-4xl">
      <div className="stellar-page-heading stellar-heading-slate mb-6 sm:mb-8">
        <span className="stellar-module-icon stellar-module-slate"><Sparkles className="h-5 w-5" /></span>
        <div>
          <h1 className="font-display text-2xl sm:text-4xl font-bold tracking-tight">Einstellungen</h1>
          <p className="text-xs sm:text-sm text-muted-foreground mt-1">Zentrale Verwaltung für Betrieb, Kommunikation und Daten.</p>
        </div>
      </div>

      <nav className="stellar-settings-nav" aria-label="Einstellungsbereiche">
        <a href="#betrieb"><Building2 className="h-4 w-4" /><span>Betrieb</span></a>
        <a href="#kommunikation"><Mail className="h-4 w-4" /><span>E-Mail</span></a>
        <a href="#bedienung"><SlidersHorizontal className="h-4 w-4" /><span>Bedienung</span></a>
        <a href="#sicherheit"><ShieldCheck className="h-4 w-4" /><span>Sicherheit</span></a>
        <a href="#daten"><Database className="h-4 w-4" /><span>Daten</span></a>
      </nav>

      <div className="stellar-settings-groups">
        <section id="betrieb" className="stellar-settings-group scroll-mt-24">
          <div className="stellar-settings-group-heading">
            <span>1</span>
            <div><h2>Betrieb & Rechnungen</h2><p>Firmendaten, Bankverbindung und Leistungen</p></div>
          </div>
          <BusinessConfigSection />
          <div className="premium-card stellar-settings-card p-5">
            <h2 className="font-display font-semibold text-lg mb-1 flex items-center gap-2">
              <Sparkles className="h-4 w-4" /> Dienste
            </h2>
            <p className="text-sm text-muted-foreground mb-3">Angebot, Preise, Standarddauer und Farbcodierung.</p>
            <Link to="/dienste" data-testid="settings-services-link">
              <Button variant="outline" size="sm">Dienste öffnen →</Button>
            </Link>
          </div>
        </section>

        <section id="kommunikation" className="stellar-settings-group scroll-mt-24">
          <div className="stellar-settings-group-heading">
            <span>2</span>
            <div><h2>Kommunikation</h2><p>E-Mail empfangen und versenden</p></div>
          </div>
          <EmailSection />
        </section>

        <section id="bedienung" className="stellar-settings-group scroll-mt-24">
          <div className="stellar-settings-group-heading">
            <span>3</span>
            <div><h2>Bedienung</h2><p>Persönlicher Einstieg in die Anwendung</p></div>
          </div>
          <div className="premium-card stellar-settings-card p-5">
            <h2 className="font-display font-semibold text-lg mb-2 flex items-center gap-2"><UserRound className="h-4 w-4" /> Konto</h2>
            <div className="text-sm text-muted-foreground">Angemeldet als <span className="font-medium text-foreground">{user?.email}</span></div>
          </div>
          <StartupPageSection />
        </section>

        <section id="sicherheit" className="stellar-settings-group scroll-mt-24">
          <div className="stellar-settings-group-heading">
            <span>4</span>
            <div><h2>Sicherheit</h2><p>Schutz sensibler Bereiche</p></div>
          </div>
          <PinSettingsSection />
        </section>

        <section id="daten" className="stellar-settings-group scroll-mt-24">
          <div className="stellar-settings-group-heading">
            <span>5</span>
            <div><h2>Daten & Wartung</h2><p>Sicherung, Import und technische Werkzeuge</p></div>
          </div>

          <div className="premium-card stellar-settings-card p-5">
            <h2 className="font-display font-semibold text-lg mb-1">Datensicherung</h2>
            <p className="text-sm text-muted-foreground mb-4">Daten als JSON-Datei exportieren oder wieder einlesen.</p>
            <div className="flex flex-wrap gap-2">
              <Button onClick={exportJson} data-testid="export-btn"><Download className="h-4 w-4 mr-1" /> JSON exportieren</Button>
              <label className="inline-flex items-center gap-2">
                <Button asChild variant="outline">
                  <span>
                    <Upload className="h-4 w-4 mr-1 inline" /> JSON importieren
                    <input ref={fileRef} type="file" accept="application/json" className="hidden" onChange={importJson} data-testid="import-input" />
                  </span>
                </Button>
              </label>
              <label className="inline-flex items-center gap-2 text-sm text-muted-foreground ml-2">
                <input type="checkbox" checked={replace} onChange={(e) => setReplace(e.target.checked)} data-testid="import-replace" />
                Vorhandene Daten ersetzen
              </label>
            </div>
            {busy && <div className="mt-3 text-sm text-muted-foreground">Import läuft…</div>}
          </div>

          <BackfillSection />

          <div className="premium-card stellar-settings-card p-5">
            <h2 className="font-display font-semibold text-lg mb-1">PWA / Offline</h2>
            <p className="text-sm text-muted-foreground">Installation über das Browser-Menü: „Zur Startseite hinzufügen“.</p>
            <p className="text-sm text-muted-foreground mt-2">Grundlegende Ansichten funktionieren offline und synchronisieren später.</p>
          </div>
        </section>
      </div>
    </div>
  );
}
